import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  IFXModal,
  IFXModalBody,
  IFXModalHeader,
  IFXModalFooter,
  IFXButton,
  IFXFieldWrapper,
  IFXTextBox,
  IFXFormLayout,
  IFXFormValidation,
  CoreValidationRuleCodes,
  IFXButtonGroup,
  IFXAccordion,
  substitute,
  isEmptyOrNull,
  useObservableCallback,
  useToast,
  AJAX_HEADER_APP_DEFAULT,
  IFXUserNote,
} from "@ifx-react/ifx-react-core";
import { ajax } from "rxjs/ajax";
import { useFormContext } from "react-hook-form";
import { UPDATE_EMAIL } from "../../../const/portal/endpoints";
import { Subject } from "rxjs";
import { mergeMap, map, catchError } from "rxjs/operators";
import { LayoutActions } from "../../../store/actions/common/actionCreators";
import { CommonToastConsts } from "../../../const/common/toastConsts";

const defaultValues = {
  emailId: "",
  confirmEmail: "",
};

const updateEmailAjax$ = ({ errorHandler }) =>
  new Subject().pipe(
    mergeMap(({ userName, emailId }) => {
      return ajax
        .post(
          UPDATE_EMAIL,
          {
            userName: userName,
            emailId: emailId,
          },
          AJAX_HEADER_APP_DEFAULT
        )
        .pipe(
          map(xhrResponse => {
            return xhrResponse.response;
          }),
          catchError(error => {
            errorHandler(error);
            return [];
          })
        );
    })
  );

const UpdateEmailForm = ({ valueMatchRule, setValueMatchRule }) => {
  const { getValues, reset } = useFormContext();

  useEffect(() => {
    setValueMatchRule({
      emailAddressMatch: ({ value, label }) => {
        const compareValue = getValues("emailId");
        const validationText =
          "Email Address and Confirm Email Address values do not match";
        if (value !== compareValue) {
          return substitute(validationText, { label });
        }
        return undefined;
      },
    });
  }, []);

  const onClear = () => {
    reset(defaultValues);
  };

  return (
    <>
      {!isEmptyOrNull(valueMatchRule) && (
        <>
          <IFXModalBody disableBackground>
            <IFXAccordion
              title=""
              isOpen={true}
              enabled={true}
              isAccordion={false}
            >
              <IFXFormLayout allFieldsColProps={{ lg: 12 }}>
                <IFXFieldWrapper
                  label="Email Address"
                  controlId="emailId"
                  name="emailId"
                  rules={[
                    CoreValidationRuleCodes.required,
                    {
                      regexCheck: [/^[^\s@]+@[^\s@]+\.[^\s@]+$/],
                    },
                  ]}
                >
                  <IFXTextBox />
                </IFXFieldWrapper>
                <IFXFieldWrapper
                  label="Confirm Email Address"
                  controlId="confirmEmail"
                  name="confirmEmail"
                  rules={[
                    CoreValidationRuleCodes.required,
                    {
                      regexCheck: [/^[^\s@]+@[^\s@]+\.[^\s@]+$/],
                    },
                    "emailAddressMatch",
                  ]}
                >
                  <IFXTextBox />
                </IFXFieldWrapper>
              </IFXFormLayout>
            </IFXAccordion>
          </IFXModalBody>
          <IFXModalFooter>
            <IFXButtonGroup className="pr-0">
              <IFXButton type="submit" />
              <IFXButton type="clear" onClick={onClear} />
            </IFXButtonGroup>
          </IFXModalFooter>
        </>
      )}
    </>
  );
};

export const UpdateEmailModal = ({
  show,
  onHide,
  username,
  otpModalShowCallBack,
}) => {
  const dispatch = useDispatch();
  const { ifxErrorToast } = useToast();
  const [valueMatchRule, setValueMatchRule] = useState({});

  const updateEmailObj$ = useMemo(() => {
    return updateEmailAjax$({
      errorHandler: error => {
        dispatch(LayoutActions.SET_LOADER_HIDE());
        ifxErrorToast(
          error?.response && error.response?.message
            ? { content: error.response.message }
            : CommonToastConsts.ERROR
        );
        
        if (
          error?.response &&
          error.response?.messageCode &&
          error.response.messageCode === "MAX_EMAIL_RETRY_UPDATE_LIMIT_REACHED"
        ) {
          onHide();
        }
      },
    });
  }, []);

  const handleOnSubmit = formValue => {
    dispatch(LayoutActions.SET_LOADER_SHOW());
    updateEmailObj$.next({
      userName: username,
      emailId: formValue.emailId,
    });
  };

  useObservableCallback(
    updateEmailObj$,
    response => {
      if (response === "success") {
        otpModalShowCallBack(true);
      }
      dispatch(LayoutActions.SET_LOADER_HIDE());
    },
    error => {
      dispatch(LayoutActions.SET_LOADER_HIDE());
    }
  );

  return (
    <>
      <IFXModal show={show} onHide={onHide} size="md">
        <IFXModalHeader>
          Provide Valid Email Address
        </IFXModalHeader>
        <IFXFormValidation
          defaultValues={defaultValues}
          onSubmit={handleOnSubmit}
          customValidationRules={valueMatchRule}
        >
          <UpdateEmailForm
            setValueMatchRule={setValueMatchRule}
            valueMatchRule={valueMatchRule}
          />
        </IFXFormValidation>
      </IFXModal>
    </>
  );
};
